import React, { useEffect, useMemo, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Select } from 'antd';
import chatStore from '../stores/chatStore';
import { useTheme } from '../theme/ThemeContext';
import { autoSizePromptTextarea, getPromptMinRows } from '../utils/promptTextarea';
import { paramDefault, paramOptions } from '../utils/mediaParams';

const IconSend = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
            d="M5 12h12M13 6l6 6-6 6"
            stroke="currentColor"
            strokeWidth="2.25"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconStop = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
        <rect x="6" y="6" width="12" height="12" rx="2" />
    </svg>
);

const VideoGenForm = observer(({ prompt, onPromptChange }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const taRef = useRef(null);
    const fileRef = useRef(null);
    const model = chatStore.videoModels.find((m) => m.id === chatStore.settings.selected_video_model);
    const params = model?.supported_parameters || {};
    const durations = useMemo(() => paramOptions(params, 'duration'), [params]);
    const aspects = useMemo(() => paramOptions(params, 'aspect_ratio'), [params]);
    const resolutions = useMemo(() => paramOptions(params, 'resolution'), [params]);
    const caps = model?.capabilities || {};
    const canAudio = Boolean(caps.audio_generation);
    const duration = chatStore.videoParams.duration || paramDefault(params, 'duration', durations[0]?.value || '');
    const aspectRatio =
        chatStore.videoParams.aspect_ratio || paramDefault(params, 'aspect_ratio', aspects[0]?.value || '16:9');
    const resolution =
        chatStore.videoParams.resolution || paramDefault(params, 'resolution', resolutions[0]?.value || '');

    useEffect(() => {
        autoSizePromptTextarea(taRef.current);
    }, [prompt]);

    useEffect(() => {
        const patch = {};
        if (!chatStore.videoParams.duration && durations[0]) patch.duration = durations[0].value;
        if (!chatStore.videoParams.aspect_ratio && aspects[0]) patch.aspect_ratio = aspects[0].value;
        if (!chatStore.videoParams.resolution && resolutions[0]) patch.resolution = resolutions[0].value;
        if (Object.keys(patch).length) chatStore.setVideoParams(patch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [model?.id, durations, aspects, resolutions]);

    const submit = async () => {
        if (!String(prompt || '').trim() || chatStore.streaming) return;
        await chatStore.generateVideo(prompt.trim());
        onPromptChange('');
    };

    const controlClass = isDark
        ? '[&_.ant-select-selector]:!bg-white/5 [&_.ant-select-selector]:!border-white/10 [&_.ant-select-selection-item]:!text-gray-100'
        : '';

    return (
        <div
            className={`overflow-hidden rounded-2xl border shadow-sm ${
                isDark ? 'border-white/10 bg-[#2f2f2f]' : 'border-black/10 bg-white'
            }`}
        >
            <input
                ref={fileRef}
                type="file"
                multiple
                className="hidden"
                accept="image/png,image/jpeg,image/jpg,image/webp,image/gif"
                onChange={(e) => {
                    Array.from(e.target.files || []).forEach((file) => chatStore.uploadAttachment(file));
                    e.target.value = '';
                }}
            />
            <div className="flex items-end gap-1.5 px-2 py-2 sm:gap-2 sm:px-3 sm:py-2.5">
                <textarea
                    ref={taRef}
                    value={prompt}
                    onChange={(e) => {
                        onPromptChange(e.target.value);
                        autoSizePromptTextarea(e.target);
                    }}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            submit();
                        }
                    }}
                    rows={getPromptMinRows()}
                    placeholder="Опишите видео…"
                    className={`bananas-msg-font box-border min-w-0 flex-1 resize-none overflow-y-hidden bg-transparent px-2 py-2 leading-normal outline-none placeholder:opacity-45 ${
                        isDark ? 'text-gray-100' : 'text-gray-900'
                    }`}
                />
                <div className="mb-0.5 shrink-0">
                    {chatStore.streaming ? (
                        <button
                            type="button"
                            onClick={() => chatStore.stop()}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-red-600 text-white"
                        >
                            <IconStop />
                        </button>
                    ) : (
                        <button
                            type="button"
                            disabled={!String(prompt || '').trim()}
                            onClick={submit}
                            className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-600 text-white disabled:opacity-35"
                        >
                            <IconSend />
                        </button>
                    )}
                </div>
            </div>
            <div
                className={`flex flex-wrap items-center gap-2 border-t px-3 py-2 ${
                    isDark ? 'border-white/10' : 'border-black/8'
                } ${controlClass}`}
            >
                <button
                    type="button"
                    title="Референс-кадр"
                    onClick={() => fileRef.current?.click()}
                    className={`flex h-8 w-8 items-center justify-center rounded-lg ${
                        isDark ? 'hover:bg-white/10' : 'hover:bg-black/5'
                    }`}
                >
                    ＋
                </button>
                {durations.length > 0 && (
                    <Select
                        size="middle"
                        value={duration || undefined}
                        placeholder="Длительность"
                        options={durations}
                        onChange={(v) => chatStore.setVideoParams({ duration: v })}
                        className="min-w-[110px]"
                        popupMatchSelectWidth={false}
                        getPopupContainer={() => document.body}
                    />
                )}
                {aspects.length > 0 && (
                    <Select
                        size="middle"
                        value={aspectRatio || undefined}
                        placeholder="Формат"
                        options={aspects}
                        onChange={(v) => chatStore.setVideoParams({ aspect_ratio: v })}
                        className="min-w-[110px]"
                        popupMatchSelectWidth={false}
                        getPopupContainer={() => document.body}
                    />
                )}
                {resolutions.length > 0 && (
                    <Select
                        size="middle"
                        value={resolution || undefined}
                        placeholder="Качество"
                        options={resolutions}
                        onChange={(v) => chatStore.setVideoParams({ resolution: v })}
                        className="min-w-[110px]"
                        popupMatchSelectWidth={false}
                        getPopupContainer={() => document.body}
                    />
                )}
                {canAudio && (
                    <label className={`flex items-center gap-1.5 text-xs ${isDark ? 'text-white/70' : 'text-black/60'}`}>
                        <input
                            type="checkbox"
                            checked={!!chatStore.videoParams.generate_audio}
                            onChange={(e) => chatStore.setVideoParams({ generate_audio: e.target.checked })}
                        />
                        Звук
                    </label>
                )}
            </div>
        </div>
    );
});

export default VideoGenForm;
